import {
  ChatBubbleOutlineOutlined,
  ErrorOutline,
  HourglassEmpty,
  MailOutline,
} from "@mui/icons-material";
import { Box, Paper, Stack, Typography } from "@mui/material";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useFetch } from "../../../hooks/Fetching";
import { useIsDesktop } from "../../../hooks/useIsDesktop";
import usePrimaryColor from "../../../hooks/usePrimaryColor";
import useUser from "../../../hooks/useUser";
import { isArrayEmpty } from "../../../utils/Functions";
import useMessageWebsocket from "../hooks/useMessageWebsocket";
import useMarkConvoAsRead from "../hooks/useMarkConvoAsRead";
import Message from "./Message";
import SendMessage from "./SendMessage";
import { SINGLE_CLINIC_USER } from "../../../enums/UrlPaths";
export const scrollType = "auto";
export default function MessagesWindow() {
  const [searchParams, setSearchParams] = useSearchParams();
  const isDesktop = useIsDesktop();
  const convoId = searchParams.get("convo");
  const hasConvoId = convoId != null && convoId !== undefined;
  const bottomRef = useRef(null);
  const shouldFetch = hasConvoId && isDesktop;
  const convoKey = `conversations_dto${convoId}`;
  const messagesKey = `messages_for_convo${convoId}`;

  const {
    data: conversationDto,
    isLoading: loadingConvos,
    error: conversationError,
  } = useFetch("/inbox/conversation/preview/", convoId, convoKey, shouldFetch);

  const {
    data: messagesData,
    isLoading,
    error,
  } = useFetch(
    "/inbox/conversation/messages/",
    convoId,
    messagesKey,
    shouldFetch
  );

  const dataEmpty =
    isArrayEmpty(messagesData) ||
    messagesData == null ||
    messagesData == undefined;
  const conversationDtoEmpty =
    isArrayEmpty(conversationDto) ||
    conversationDto == null ||
    conversationDto == undefined;

  const { profile} = useUser();
  const isReceiver = Number(profile.id) === Number(conversationDto?.receiverId);
  const messagesToUpdate = useMessageWebsocket(convoId, !isReceiver);
  useMarkConvoAsRead(convoId, shouldFetch && !isLoading && !error && messagesData != null);

  const messages = useMemo(() => {
    return messagesData?.map((msg, idx) => {
      const update = messagesToUpdate.includes(msg?.id);
      return <Message key={msg.id} msg={msg} idx={idx} update={update} />;
    });
  }, [messagesData, messagesToUpdate]);

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: scrollType });
    }
  }, [messagesData?.length]);

  if (!isDesktop) return null;

  if (!hasConvoId) {
    return (
      <StatusMessage
        type="noConvo"
        title="NoConversationSelected"
        subtitle="ClickOnAConversationOrStartNew"
      />
    );
  }

  if (isLoading || loadingConvos) {
    return <StatusMessage type="loading" title="LoadingConversation" />;
  }

  if (error || conversationError) {
    searchParams.delete("convo");
    setSearchParams(searchParams);
    return (
      <StatusMessage
        type="error"
        title="SomethingWentWrong"
        subtitle="PleaseTryAgainLaterOrRefreshThePage."
      />
    );
  }

  if (dataEmpty || conversationDtoEmpty) {
    return (
      <Wrapper conversationDto={conversationDto} bottomRef={bottomRef}>
        <StatusMessage
          type="noData"
          title="NoMessagesYet"
          subtitle="StartTheConversation"
        />
      </Wrapper>
    );
  }

  return (
    <Wrapper bottomRef={bottomRef} conversationDto={conversationDto}>
      {messages}
    </Wrapper>
  );
}

function Wrapper({ children, conversationDto, bottomRef }) {
  const color = usePrimaryColor();
  const { t } = useTranslation();
  const { roles } = useUser();

  const isMasterSender = conversationDto?.senderEmail === "System Info";
  const isReceiverOrSenderDeleted=conversationDto?.deletedUsersInConversation;

  return (
    <>
      <Box
        sx={{
          p: 2,
          pb: 4.5,
          borderBottom: `1px solid ${color}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography variant="body2" color="text.secondary">
            {t("To")}:{" "}
            {!roles?.user ? (
              <a
                key={conversationDto?.receiverEmail}
                href={SINGLE_CLINIC_USER + conversationDto?.receiverId}
                rel="noopener noreferrer"
                style={{ color: color, textDecoration: "underline" }}
              >
                {conversationDto?.receiverEmail}
              </a>
            ) : (
              conversationDto?.receiverEmail
            )}
          </Typography>

          <Typography variant="h5" fontWeight="bold">
            {t("From")}: {conversationDto?.senderEmail}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ flexGrow: 1, p: 3, overflowY: "auto" }}>
        {children}
        <div ref={bottomRef} />
      </Box>

      <SendMessage
        isMasterSender={isMasterSender}
        scrollToBottom={() =>
          bottomRef.current?.scrollIntoView({ behavior: scrollType })
        }
        isReceiverOrSenderDeleted={isReceiverOrSenderDeleted}
      />
    </>
  );
}

function StatusMessage({ type = "noData", title, subtitle }) {
  const { t } = useTranslation();
  const color = usePrimaryColor();

  const icons = {
    loading: <HourglassEmpty color="disabled" fontSize="large" />,
    error: <ErrorOutline color="error" fontSize="large" />,
    noData: <MailOutline sx={{ color: color }} fontSize="large" />,
    noConvo: (
      <ChatBubbleOutlineOutlined sx={{ color: color }} fontSize="large" />
    ),
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: 6,
      }}
    >
      <Stack
        spacing={2}
        alignItems="center"
        justifyContent="center"
        sx={{ px: 2 }}
      >
        {icons[type]}
        <Typography variant="h6" color="text.secondary">
          {t(title)}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" align="center">
            {t(subtitle)}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
